import * as React from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
};

type ThemeProviderState = {
	theme: Theme;
	setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
	theme: "light",
	setTheme: () => null,
};

const ThemeProviderContext = React.createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
	children,
	// Light, not "system": the app follows the OS only when the user picks
	// "Hệ thống" from the switcher. A first-time visitor on a dark-themed OS
	// otherwise lands in dark mode without ever having asked for it.
	defaultTheme = "light",
	storageKey = "ui-theme",
	...props
}: ThemeProviderProps) {
	const [theme, setTheme] = React.useState<Theme>(
		() => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
	);

	React.useEffect(() => {
		const root = window.document.documentElement;

		const applyTheme = (mode: "light" | "dark") => {
			root.classList.remove("light", "dark");
			root.classList.add(mode);
		};

		if (theme === "system") {
			const media = window.matchMedia("(prefers-color-scheme: dark)");

			applyTheme(media.matches ? "dark" : "light");

			// Keep following the OS setting live while the app stays open in
			// "system" mode, instead of only reading it once at mount.
			const handleChange = (event: MediaQueryListEvent) => {
				applyTheme(event.matches ? "dark" : "light");
			};

			media.addEventListener("change", handleChange);

			return () => media.removeEventListener("change", handleChange);
		}

		applyTheme(theme);
	}, [theme]);

	const value = {
		theme,
		setTheme: (theme: Theme) => {
			localStorage.setItem(storageKey, theme);
			setTheme(theme);
		},
	};

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useTheme = () => {
	const context = React.useContext(ThemeProviderContext);

	if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider");

	return context;
};
